import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as CryptoJS from 'crypto-js';
import { Subject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { AuthData } from '@classes/auth/auth-data';
import { AuthUser } from '@classes/auth/auth-user';
import { AuthResponse } from '@classes/auth/auth-response';
import { LicenceRoleType } from '@enums/licence-role-type.enum';
import { UiService } from './ui.service';
import { LanguageService } from './language.service';
import { AppConfigService } from './app-config.service';
import { SimulationService } from './simulation.service';
import { Token } from '@classes/token';
import { ProviderType } from '@enums/provider-type.enum';
import { HelpService } from './help.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    authChanged = new Subject<boolean>();
    authUserChanged = new Subject<AuthUser>();

    private identityApiUrl: string = environment.identityApiUrl;
    private apiUrl: string = environment.apiUrl;
    private tokenKey: string = environment.tokenKey;
    private rfTokenKey: string = environment.rfTokenKey;
    private tokenExpKey: string = environment.tokenExpKey;
    private authUser: AuthUser;
    private isMaintenanceMode: boolean;

    public redirectUrl: string;

    constructor(
        private configSrv: AppConfigService,
        private router: Router,
        private http: HttpClient,
        private jwtHelperSrv: JwtHelperService,
        private uiSrv: UiService,
        private languageSrv: LanguageService,
        private simuSrv: SimulationService,
        private helpSrv: HelpService
    ) {
        this.isMaintenanceMode = configSrv.isMaintenance;
    }

    login(authData: AuthData): void {
        const hashedPassword = this.cryptString(authData.password);

        const httpParams = new HttpParams()
            .append("username", authData.username)
            .append("password", hashedPassword)
            .append("grant_type", "password")
            .append("scope", "api offline_access")
            .append("client_id", "web");

        this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
        .subscribe(result => {
            this.languageSrv.setCurrentLanguage();

            this.saveToken(result.access_token, result.refresh_token, result.expires_in);
            this.checkLicenceValidity();
            this.setUserFromToken();

            this.authUserChanged.next(this.authUser);
            this.authChanged.next(true);

            // this.helpSrv.hasChangLog().subscribe(hasChangeLog =>
            // {
            //     if (hasChangeLog)
            //         this.redirectUrl = '/changelog';
                if (!this.redirectUrl)
                    this.redirectUrl = '/';
                this.router.navigate([this.redirectUrl]);
                this.redirectUrl = null;
            // });
        });
    }

    updatePassword(resetData: {currentPassword: string, newPassword: string}): Observable<string> {
        const hashedCurrentPassword = this.cryptString(resetData.currentPassword);
        const hashedNewPassword = this.cryptString(resetData.newPassword);

        const httpParams = new HttpParams()
            .append("current_password", hashedCurrentPassword)
            .append("new_password", hashedNewPassword);

        return this.http.post<string>(this.apiUrl + 'users/password', httpParams)
        .pipe(
            tap(() => {
                this.afterPwdReset(resetData.newPassword);
            })
        );
    }

    requestPasswordReset(username: string): Observable<Object> {
        const httpParams = new HttpParams().append("username", username);

        return this.http.post(this.apiUrl + 'users/password-reset-request', httpParams);
    }

    requestPasswordInit(username: string): Observable<Object> {
        const httpParams = new HttpParams()
            .append("username", username);

        return this.http.post(this.apiUrl + 'users/password-init-request', httpParams);
    }

    resetPassword(userId: string, token: string, newPassword: string, isSubscribedNewsletter: boolean = null): Observable<Object> {
        const hashedNewPassword = this.cryptString(newPassword);

        // Send data in body in place of http params to avoid mess in token serialisation due to special characters
        const body = {
            token: token,
            new_password: hashedNewPassword,
            isSubscribedNewsletter: isSubscribedNewsletter
        };

        return this.http.post(`${this.apiUrl}users/${userId}/password-reset`, body)
        .pipe(
            tap(() => {
                this.router.navigate(["/login"]);
            })
        );
    }

    refreshToken(): Observable<AuthResponse> {
        const refreshToken = localStorage.getItem(this.rfTokenKey);

        const httpParams = new HttpParams()
            .append("grant_type", "refresh_token")
            .append("scope", "api offline_access")
            .append("client_id", "web")
            .append("refresh_token", refreshToken);

        return this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
        .pipe(
            tap(result => {
                this.saveToken(result.access_token, result.refresh_token, result.expires_in);
                this.checkLicenceValidity();
                this.setUserFromToken();
                this.authUserChanged.next(this.authUser);
                this.authChanged.next(true);
            })
        );
    }

    logout(): void {
        localStorage.clear();
        this.simuSrv.clearLocal();
        this.authChanged.next(false);
        this.authUserChanged.next(null);
        this.router.navigate(['/login']);
    }

    isAuth(): boolean {
        const decodedToken = this.getDecodedToken();

        if(!decodedToken || decodedToken.upd_pwd_req === true) {
            return false;
        }

        return !this.isTokenExpired();
    }

    getDecodedToken(): Token {
        const token = this.jwtHelperSrv.tokenGetter();
        if(token) {
            return this.jwtHelperSrv.decodeToken(token);
        }

        return null;
    }

    getAuthUser(): AuthUser {
        if(!this.authUser) {
            this.setUserFromToken();
        }

        return this.authUser;
    }

    setAuthUser(user: AuthUser): void {
        this.authUser = user;
        this.authUserChanged.next(this.authUser);
    }

    isAuthorized(roles?: LicenceRoleType[], providers?: ProviderType[]): boolean {
        const token = this.getDecodedToken();

        if(!token) {
            return false;
        }

        // role not authorised
        if(roles && roles.indexOf(token.role) === -1) {
            return false;
        }

        // provider not authorised
        if(providers && providers.indexOf(token.provider_category) === -1) {
            return false;
        }

        // authorised
        return true;
    }

    private setUserFromToken(): void {
        const decodedToken = this.getDecodedToken();
        if(!decodedToken) {
            return null;
        }

        this.authUser = new AuthUser(decodedToken.name, decodedToken.role, decodedToken.provider_category, decodedToken.language);
    }

    private saveToken(accessToken: string, refreshToken: string, deadline: number): void {
        const tokenExpDate = new Date();
        tokenExpDate.setSeconds(tokenExpDate.getSeconds() + deadline);

        localStorage.setItem(this.tokenExpKey, tokenExpDate.toString());
        localStorage.setItem(this.tokenKey, accessToken);
        localStorage.setItem(this.rfTokenKey, refreshToken);
    }

    private checkLicenceValidity(): void {
        const decodedToken = this.getDecodedToken();

        if(this.isMaintenanceMode && decodedToken.role !== LicenceRoleType.bzd && decodedToken.role !== LicenceRoleType.bko) {
            this.logout();
        } else if(!decodedToken.licence_valid) {
            this.uiSrv.showSnackbar('auth.licenceInvalid.summary', true, 5000);
        }

    }

    private afterPwdReset(newPassword: string): void {
        const decodedToken = this.getDecodedToken();

        if(decodedToken) {
            this.login({username: decodedToken.name, password: newPassword});
        } else {
            this.logout();
        }
    }

    private isTokenExpired(): boolean {
        const tokenExpDate = new Date(localStorage.getItem(this.tokenExpKey));
        const now = new Date();
        return tokenExpDate < now;
    }

    private cryptString(stringTocrypt: string): string {
        return CryptoJS.SHA256(stringTocrypt).toString();
    }
}
